// ConsentBanner.jsx
// GDPR/CCPA cookie and data consent banner.
// Shown once to new visitors; choice is persisted to localStorage under "orbit_consent".
// "orbit_consent" values: "accepted" | "declined"
//
// When declined, no analytics or non-essential cookies are loaded.
// The banner re-appears whenever localStorage is cleared (e.g. new browser).
import { useState, useEffect } from "react";
import { T } from "../../theme.js";

const STORAGE_KEY = "orbit_consent";

export function useConsent() {
  const [consent, setConsent] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
  });

  function accept() {
    try { localStorage.setItem(STORAGE_KEY, "accepted"); } catch {}
    setConsent("accepted");
  }

  function decline() {
    try { localStorage.setItem(STORAGE_KEY, "declined"); } catch {}
    setConsent("declined");
  }

  return { consent, accept, decline };
}

export default function ConsentBanner({ onAccept, onDecline, onShowPrivacy }) {
  const [visible, setVisible] = useState(false);

  // Delay mount slightly so the banner doesn't flash during initial render
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 400);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-label="Cookie and data consent"
      style={{
        position: "fixed",
        bottom: 20,
        left: "50%",
        transform: "translateX(-50%)",
        width: "min(560px, calc(100vw - 32px))",
        background: T.surface,
        border: `1px solid ${T.edge}`,
        borderRadius: 16,
        boxShadow: "0 8px 40px rgba(30,16,8,0.18)",
        padding: "18px 20px",
        zIndex: 9900,
        display: "flex",
        flexDirection: "column",
        gap: 14,
        fontFamily: "'DM Sans', system-ui, sans-serif",
        animation: "slideUp 0.25s ease",
      }}
    >
      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>

      <div>
        <p style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>
          Your privacy matters
        </p>
        <p style={{ fontSize: 13, color: T.muted, lineHeight: 1.55 }}>
          Orbit uses essential cookies to keep you signed in and functional cookies to
          improve your experience. We do not sell your data.{" "}
          <button
            type="button"
            onClick={onShowPrivacy}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: T.clay, fontSize: 13, textDecoration: "underline", padding: 0,
            }}
            aria-label="Read our Privacy Policy"
          >
            Privacy Policy
          </button>
          .
        </p>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onAccept}
          style={{
            flex: "1 1 140px",
            padding: "10px 16px",
            borderRadius: 10,
            border: "none",
            cursor: "pointer",
            background: `linear-gradient(135deg, ${T.bark}, ${T.clay})`,
            color: "white",
            fontWeight: 600,
            fontSize: 13,
          }}
          aria-label="Accept all cookies"
        >
          Accept all
        </button>
        <button
          type="button"
          onClick={onDecline}
          style={{
            flex: "1 1 140px",
            padding: "10px 16px",
            borderRadius: 10,
            border: `1px solid ${T.edge}`,
            cursor: "pointer",
            background: "transparent",
            color: T.muted,
            fontWeight: 500,
            fontSize: 13,
          }}
          aria-label="Accept essential cookies only"
        >
          Essential only
        </button>
      </div>
    </div>
  );
}
